<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">Bulk Edit Policies</span>
    </div>

    <div class="d-flex justify-content-center align-items-center pt-35-px" v-if="isLoading">
      <Spinner />
    </div>

    <transition name="slide-up">
      <div v-if="!isLoading" class="p-20-px flex-grow-1 overflow-y-auto">
        <!-- Policy Selection Section -->
        <a-row>
          <a-col :span="24">
            <div class="rule-list">
              <div class="section-header-row">
                <div class="ant-form-item-label">
                  <label>Select Policies</label>
                </div>
                <div class="header-actions">
                  <a-button 
                    v-if="selectedPolicies.length > 0" 
                    type="danger" 
                    class="mr-3"
                    :loading="isDeleting"
                    @click="showDeleteConfirm"
                  >
                    <a-icon type="delete" />
                    Delete Selected ({{ selectedPolicies.length }})
                  </a-button>
                  <a-tag v-if="selectedPolicies.length > 0" color="blue" class="selected-tag">
                    {{ selectedPolicies.length }} selected
                  </a-tag>
                </div>
              </div>
              <div class="policy-table-wrapper mt-3">
                <PaginationTable
                  :pagination="pagin"
                  :columns="BulkPolicySelectColumn"
                  :dataSource="policies"
                  :tableSize="tableSize"
                  :hasSelectRow="true"
                  @changeSelectedRow="onPolicySelectionChange"
                />
              </div>
              <div class="pagination-wrapper mt-3">
                <PaginationBar 
                  :pageSize.sync="pagin.pageSize" 
                  :total="pagin.total" 
                  @change="paginChange" 
                />
              </div>
            </div>
          </a-col>
        </a-row>

        <!-- Common Rules Section (shows when multiple policies selected) -->
        <a-row class="mt-4" v-if="selectedPolicies.length > 1 && commonRules.length > 0">
          <a-col :span="24">
            <div class="rule-list common-rules-section">
              <div class="section-header-row">
                <div class="ant-form-item-label">
                  <label>
                    Common Rules
                    <a-tooltip placement="top">
                      <template slot="title">
                        <span>Rules that exist in ALL selected policies. You can delete these from all policies at once.</span>
                      </template>
                      <a-icon type="info-circle" theme="filled" class="ml-2" />
                    </a-tooltip>
                  </label>
                </div>
                <a-tag color="green">{{ commonRules.length }} common rules found</a-tag>
              </div>
              <table class="full-w mt-4">
                <thead>
                  <tr>
                    <th class="w-50">No.</th>
                    <th class="w-300">Element</th>
                    <th class="w-200">Condition</th>
                    <th>Keyword</th>
                    <th class="w-200">Action</th>
                    <th class="w-50"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(rule, index) in commonRules" :key="'common-' + index" :class="{ 'marked-for-delete': rule.markedForDelete }">
                    <td>{{ index + 1 }}</td>
                    <td>
                      <span class="rule-value">{{ getRuleTypeName(rule.type) }}</span>
                    </td>
                    <td>
                      <span class="rule-value">{{ getConditionName(rule.condition) }}</span>
                    </td>
                    <td>
                      <span class="rule-value">{{ rule.field }}</span>
                    </td>
                    <td>
                      <span class="rule-value">{{ getActionName(rule.action) }}</span>
                    </td>
                    <td>
                      <a-button 
                        size="large" 
                        :class="['delete-toggle-btn', { 'restore-btn': rule.markedForDelete }]"
                        @click="toggleDeleteCommonRule(index)"
                      >
                        <a-icon :type="rule.markedForDelete ? 'undo' : 'delete'" />
                      </a-button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="rulesToDelete.length > 0" class="delete-warning mt-3">
                <a-icon type="warning" theme="filled" class="mr-2" />
                {{ rulesToDelete.length }} rule(s) will be removed from all selected policies
              </div>
            </div>
          </a-col>
        </a-row>

        <!-- No Common Rules Message -->
        <a-row class="mt-4" v-if="selectedPolicies.length > 1 && commonRules.length === 0 && !isAnalyzing">
          <a-col :span="24">
            <a-alert
              message="No Common Rules"
              description="The selected policies don't share any common rules."
              type="info"
              show-icon
            />
          </a-col>
        </a-row>

        <!-- Analyzing Indicator -->
        <a-row class="mt-4" v-if="isAnalyzing">
          <a-col :span="24">
            <div class="analyzing-box">
              <a-spin size="small" class="mr-2" />
              Analyzing selected policies for common rules...
            </div>
          </a-col>
        </a-row>

        <!-- Rules to Add Section -->
        <a-row class="mt-4">
          <a-col :span="24">
            <div class="rule-list">
              <div class="section-header-row">
                <div class="ant-form-item-label">
                  <label>Rules to Add</label>
                </div>
                <a-button class="a-header--button btn-secondary" type="primary" @click="onAddRule">
                  <img class="mr-2 icon-size" src="@assets/icon/plus.svg" />
                  Add New Rule
                </a-button>
              </div>
              <table class="full-w mt-4" v-if="rules.length > 0">
                <thead>
                  <tr>
                    <th class="w-50">No.</th>
                    <th class="w-300">Element</th>
                    <th class="w-200">Condition</th>
                    <th>Keyword</th>
                    <th class="w-200">Action</th>
                    <th class="w-50"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(rule, index) in rules" :key="index">
                    <td>{{ index + 1 }}</td>
                    <td>
                      <a-form-item class="mb-0">
                        <a-select v-model="rule.type" size="large">
                          <a-select-option v-for="type in ruleTypes" :key="type.id" :value="type.id">
                            {{ type.name }}
                          </a-select-option>
                        </a-select>
                      </a-form-item>
                    </td>
                    <td>
                      <a-form-item class="mb-0">
                        <a-select v-model="rule.condition" size="large">
                          <a-select-option v-for="condition in conditions" :key="condition.id" :value="condition.id">
                            {{ condition.name }}
                          </a-select-option>
                        </a-select>
                      </a-form-item>
                    </td>
                    <td>
                      <a-form-item class="mb-0">
                        <a-input v-model="rule.field" placeholder="Enter keyword" size="large" />
                      </a-form-item>
                    </td>
                    <td>
                      <a-form-item class="mb-0">
                        <a-select v-model="rule.action" size="large">
                          <a-select-option v-for="action in actions" :key="action.id" :value="action.id">
                            {{ action.name }}
                          </a-select-option>
                        </a-select>
                      </a-form-item>
                    </td>
                    <td>
                      <a-button size="large" class="remove-btn" @click="onRemoveRule(index)">
                        <img src="@assets/icon/remove.svg" />
                      </a-button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="empty-rules-message mt-3">
                <a-icon type="info-circle" class="mr-2" />
                No rules to add. Click "Add New Rule" to create rules that will be appended to selected policies.
              </div>
            </div>
          </a-col>
        </a-row>

        <!-- Keywords Section -->
        <div class="ant-form-item-label mt-4">
          <label>Full Page HTML (Keyword Monitoring)</label>
        </div>
        <a-row :gutter="20">
          <a-col :span="12">
            <a-form-item>
              <template slot="label">
                Blacklisted keyword(s) to Add
                <a-tooltip placement="top">
                  <template slot="title">
                    <span class="fs-10">
                      Keywords that should NOT appear on webpages. These will be appended to existing blacklist.
                    </span>
                  </template>
                  <a-icon type="info-circle" theme="filled" />
                </a-tooltip>
              </template>
              <a-textarea
                v-model="blacklistKeywords"
                class="row-6 mb-0"
                :rows="6"
                placeholder="Enter keywords separated by commas"
              />
              <span class="fs-10">Separate different keywords by a comma (,). Keywords are case-insensitive.</span>
            </a-form-item>
          </a-col>
          <a-col :span="12">
            <a-form-item>
              <template slot="label">
                Whitelisted keyword(s) to Add
                <a-tooltip placement="top">
                  <template slot="title">
                    <span class="fs-10">
                      Keywords that SHOULD appear on webpages. These will be appended to existing whitelist.
                    </span>
                  </template>
                  <a-icon type="info-circle" theme="filled" />
                </a-tooltip>
              </template>
              <a-textarea
                v-model="whitelistKeywords"
                class="row-6 mb-0"
                :rows="6"
                placeholder="Enter keywords separated by commas"
              />
              <span class="fs-10">Separate different keywords by a comma (,). Keywords are case-insensitive.</span>
            </a-form-item>
          </a-col>
        </a-row>

        <!-- Progress Section -->
        <a-row class="mt-4" v-if="isSaving">
          <a-col :span="24">
            <div class="rule-list">
              <div class="ant-form-item-label">
                <label>Update Progress</label>
              </div>
              <a-progress
                :percent="Math.round((updateProgress.completed / updateProgress.total) * 100)"
                :status="updateProgress.failed > 0 ? 'exception' : 'active'"
              />
              <p class="progress-text mt-2 mb-0">
                Updating {{ updateProgress.completed }} of {{ updateProgress.total }} policies...
                <span v-if="updateProgress.failed > 0" class="text-danger">
                  ({{ updateProgress.failed }} failed)
                </span>
              </p>
            </div>
          </a-col>
        </a-row>
      </div>
    </transition>

    <!-- Footer -->
    <div class="a-footer">
      <a-button key="back" size="large" class="mr-3 slide-down-enter-active" @click="onCancel">
        Cancel
      </a-button>
      <a-button
        key="submit"
        size="large"
        type="primary"
        class="slide-down-enter-active"
        :disabled="!canSave"
        :loading="isSaving"
        @click="onSave"
      >
        Save Changes
      </a-button>
    </div>
  </div>
</template>

<script>
import { RULE_TYPES, RULE_ACTIONS, RULE_CONDITIONS } from '@constants/rules';
import { BulkPolicySelectColumn } from '../columnsTable/BulkPolicySelectColumn';
import PolicyService from '../services/policy.service';

export default {
  name: 'BulkRulesEdit',
  data() {
    return {
      BulkPolicySelectColumn,
      ruleTypes: RULE_TYPES,
      actions: RULE_ACTIONS,
      conditions: RULE_CONDITIONS,
      policies: [],
      selectedPolicies: [],
      selectedPoliciesData: [],
      commonRules: [],
      rules: [],
      blacklistKeywords: '',
      whitelistKeywords: '',
      isLoading: true,
      isAnalyzing: false,
      isSaving: false,
      isDeleting: false,
      updateProgress: {
        total: 0,
        completed: 0,
        failed: 0
      },
      pagin: {
        current: 1,
        pageSize: Number(window.localStorage.getItem('pageSize')) || 25,
        total: 0
      },
      tableSize: {
        height: '250',
        width: ''
      }
    };
  },
  computed: {
    canSave() {
      const hasSelection = this.selectedPolicies.length > 0;
      const hasAdditions = this.rules.length > 0 || 
                          this.blacklistKeywords.trim() !== '' || 
                          this.whitelistKeywords.trim() !== '';
      const hasDeletions = this.rulesToDelete.length > 0;
      return hasSelection && (hasAdditions || hasDeletions) && !this.isSaving;
    },
    rulesToDelete() {
      return this.commonRules.filter(r => r.markedForDelete);
    }
  },
  mounted() {
    this.loadPolicies();
  },
  methods: {
    async loadPolicies() {
      try {
        const data = await PolicyService.getList();
        this.policies = (data || []).map((item) => ({
          ...item,
          id: item.id,
          urlCount: item.urls ? item.urls.length : 0,
          rulesCount: item.rules ? item.rules.length : 0,
          isChecked: false
        }));
        this.pagin.total = this.policies.length;
      } catch {
        this.$showErrorMessage('Failed to load policies');
      } finally {
        setTimeout(() => {
          this.isLoading = false;
        }, 300);
      }
    },
    paginChange(pagin) {
      this.pagin = { ...pagin };
    },
    async onPolicySelectionChange(selectedIds) {
      this.selectedPolicies = selectedIds;
      
      if (selectedIds.length > 1) {
        await this.analyzeCommonRules();
      } else {
        this.commonRules = [];
        this.selectedPoliciesData = [];
      }
    },
    async analyzeCommonRules() {
      this.isAnalyzing = true;
      this.commonRules = [];
      
      try {
        // Fetch full data for all selected policies
        const policyPromises = this.selectedPolicies.map(id => PolicyService.get(id));
        this.selectedPoliciesData = await Promise.all(policyPromises);
        
        // Find common rules
        if (this.selectedPoliciesData.length > 0) {
          const firstPolicyRules = this.selectedPoliciesData[0].rules || [];
          
          // Check each rule from first policy against all other policies
          const common = firstPolicyRules.filter(rule => {
            return this.selectedPoliciesData.every(policy => {
              const policyRules = policy.rules || [];
              return policyRules.some(pr => this.rulesMatch(pr, rule));
            });
          });
          
          this.commonRules = common.map(rule => ({
            ...rule,
            markedForDelete: false
          }));
        }
      } catch (error) {
        console.error('Error analyzing common rules:', error);
        this.$showErrorMessage('Failed to analyze selected policies');
      } finally {
        this.isAnalyzing = false;
      }
    },
    rulesMatch(rule1, rule2) {
      return rule1.type === rule2.type &&
             rule1.condition === rule2.condition &&
             rule1.field === rule2.field &&
             rule1.action === rule2.action;
    },
    toggleDeleteCommonRule(index) {
      this.$set(this.commonRules, index, {
        ...this.commonRules[index],
        markedForDelete: !this.commonRules[index].markedForDelete
      });
    },
    getRuleTypeName(typeId) {
      const type = this.ruleTypes.find(t => t.id === typeId);
      return type ? type.name : typeId;
    },
    getConditionName(conditionId) {
      const condition = this.conditions.find(c => c.id === conditionId);
      return condition ? condition.name : conditionId;
    },
    getActionName(actionId) {
      const action = this.actions.find(a => a.id === actionId);
      return action ? action.name : actionId;
    },
    onAddRule() {
      this.rules.push({
        type: 'HREF',
        condition: 'Contains',
        field: '',
        action: 'Do not monitor',
        severity: (this.rules.length + 1) * 10
      });
    },
    onRemoveRule(index) {
      this.rules.splice(index, 1);
      this.rules.forEach((rule, idx) => {
        rule.severity = (idx + 1) * 10;
      });
    },
    mergeKeywords(existing, newKeywords) {
      if (!newKeywords || newKeywords.trim() === '') {
        return existing || '';
      }
      
      const existingArray = existing 
        ? existing.split(',').map(k => k.trim().toLowerCase()).filter(k => k)
        : [];
      const newArray = newKeywords.split(',').map(k => k.trim()).filter(k => k);
      
      const merged = [...existingArray];
      newArray.forEach(keyword => {
        if (!existingArray.includes(keyword.toLowerCase())) {
          merged.push(keyword);
        }
      });
      
      return merged.join(', ');
    },
    filterOutDeletedRules(existingRules) {
      if (this.rulesToDelete.length === 0) {
        return existingRules;
      }
      
      return existingRules.filter(rule => {
        return !this.rulesToDelete.some(deleteRule => this.rulesMatch(rule, deleteRule));
      });
    },
    async onSave() {
      if (!this.canSave) return;

      // Validate rules
      const invalidRules = this.rules.filter(rule => !rule.field || rule.field.trim() === '');
      if (invalidRules.length > 0) {
        this.$showErrorMessage('Please fill in all rule keywords');
        return;
      }

      this.isSaving = true;
      this.updateProgress = {
        total: this.selectedPolicies.length,
        completed: 0,
        failed: 0
      };

      const results = { success: [], failed: [] };

      for (const policyId of this.selectedPolicies) {
        try {
          const currentPolicy = await PolicyService.get(policyId);
          
          // Filter out rules marked for deletion
          let updatedRules = this.filterOutDeletedRules(currentPolicy.rules || []);
          
          // Prepare new rules with correct severity values
          const existingRulesCount = updatedRules.length;
          const newRulesWithSeverity = this.rules.map((rule, index) => ({
            ...rule,
            severity: (existingRulesCount + index + 1) * 10
          }));

          // Reassign severity to existing rules after filtering
          updatedRules = updatedRules.map((rule, index) => ({
            ...rule,
            severity: (index + 1) * 10
          }));

          const updatedPolicy = {
            name: currentPolicy.name,
            description: currentPolicy.description,
            text_string_appear: this.mergeKeywords(currentPolicy.text_string_appear, this.whitelistKeywords),
            text_string_not_appear: this.mergeKeywords(currentPolicy.text_string_not_appear, this.blacklistKeywords),
            rules: [...updatedRules, ...newRulesWithSeverity],
            url_ids: currentPolicy.urls ? currentPolicy.urls.map(u => u.id.toString()) : []
          };

          await PolicyService.update(policyId, updatedPolicy);
          results.success.push(policyId);
        } catch (error) {
          console.error(`Failed to update policy ${policyId}:`, error);
          results.failed.push(policyId);
          this.updateProgress.failed++;
        }
        
        this.updateProgress.completed++;
      }

      this.isSaving = false;

      if (results.failed.length === 0) {
        this.$showSuccessMessage(`Successfully updated ${results.success.length} policies`);
        this.$router.push('/policy/list');
      } else if (results.success.length > 0) {
        this.$showWarningMessage(
          `Updated ${results.success.length} policies, but ${results.failed.length} failed`
        );
      } else {
        this.$showErrorMessage('Failed to update all policies');
      }
    },
    showDeleteConfirm() {
      const count = this.selectedPolicies.length;
      const policyNames = this.policies
        .filter(p => this.selectedPolicies.includes(p.id))
        .map(p => p.name)
        .slice(0, 5);
      
      const namesList = policyNames.join(', ') + (count > 5 ? ` and ${count - 5} more...` : '');
      
      this.$confirm({
        title: `Delete ${count} ${count === 1 ? 'Policy' : 'Policies'}?`,
        content: `Are you sure you want to delete the following policies? This action cannot be undone.\n\n${namesList}`,
        okText: 'Yes, Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => {
          this.bulkDeletePolicies();
        }
      });
    },
    async bulkDeletePolicies() {
      this.isDeleting = true;
      
      const results = { success: [], failed: [] };
      
      for (const policyId of this.selectedPolicies) {
        try {
          await PolicyService.delete(policyId);
          results.success.push(policyId);
        } catch (error) {
          console.error(`Failed to delete policy ${policyId}:`, error);
          results.failed.push(policyId);
        }
      }
      
      this.isDeleting = false;
      
      if (results.failed.length === 0) {
        this.$showSuccessMessage(`Successfully deleted ${results.success.length} policies`);
        // Refresh the policy list and reset selection
        this.selectedPolicies = [];
        this.commonRules = [];
        await this.loadPolicies();
      } else if (results.success.length > 0) {
        this.$showWarningMessage(
          `Deleted ${results.success.length} policies, but ${results.failed.length} failed`
        );
        // Refresh anyway to show updated list
        this.selectedPolicies = [];
        this.commonRules = [];
        await this.loadPolicies();
      } else {
        this.$showErrorMessage('Failed to delete policies');
      }
    },
    onCancel() {
      this.$router.push('/policy/list');
    }
  }
};
</script>

<style lang="scss" scoped>
.rule-list {
  background-color: #f7f7f7;
  padding: 20px;
  border-radius: 4px;

  .ant-form-item-label {
    label::after {
      content: '';
    }
  }

  td {
    padding: 5px;
  }
}

.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
}

.selected-tag {
  font-size: 14px;
}

.policy-table-wrapper {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  background: #fff;
}

.common-rules-section {
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
}

.rule-value {
  display: inline-block;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  min-width: 120px;
}

.marked-for-delete {
  background-color: #fff1f0;
  
  .rule-value {
    text-decoration: line-through;
    color: #999;
  }
}

.delete-toggle-btn {
  width: 40px;
  padding: 0;
  border-color: #ff4d4f;
  color: #ff4d4f;
  
  &:hover {
    background-color: #ff4d4f;
    color: #fff;
  }
}

.restore-btn {
  border-color: #52c41a;
  color: #52c41a;
  
  &:hover {
    background-color: #52c41a;
    color: #fff;
  }
}

.delete-warning {
  color: #ff4d4f;
  background-color: #fff1f0;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ffa39e;
}

.analyzing-box {
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
  padding: 12px 16px;
  border-radius: 4px;
  color: #1890ff;
}

.remove-btn {
  width: 40px;
  padding: 0;
  border-color: $rd;
}

.empty-rules-message {
  color: #999;
  padding: 16px;
  text-align: center;
  background: #fff;
  border: 1px dashed #d9d9d9;
  border-radius: 4px;
}

.progress-text {
  color: #666;
}

.text-danger {
  color: #ff4d4f;
}

td, th {
  padding: 0 10px;
}

table {
  border-collapse: collapse;
  
  th {
    background-color: #fafafa;
    font-weight: 600;
    padding: 12px 10px;
    text-align: left;
    border-bottom: 1px solid #e8e8e8;
  }
  
  td {
    padding: 8px 10px;
    border-bottom: 1px solid #e8e8e8;
    vertical-align: middle;
  }
  
  tbody tr:hover {
    background-color: #fafafa;
  }
}

.w-50 {
  width: 50px;
}

.w-200 {
  width: 200px;
}

.w-300 {
  width: 300px;
}

.full-w {
  width: 100%;
}

::v-deep .ant-form-item {
  margin-bottom: 0;
}

::v-deep .ant-form-explain {
  margin-bottom: -7px;
}

.pagination-wrapper {
  background: #fff;
  padding: 12px 16px;
  border: 1px solid #e8e8e8;
  border-top: none;
  border-radius: 0 0 4px 4px;
}
</style>
